import { FC, ReactElement } from 'react';

import { BackToGameConnected } from '@pp/core/src/components/backToGame/BackToGameConnected';
import { BackToGameHolder } from '@pp/core/src/components/backToGame/BackToGameHolder';
import { JurisdictionNL } from '@pp/core/src/components/compliance/JurisdictionNL';
import { SessionBalanceConnected } from '@pp/core/src/components/compliance/SessionBalanceConnected';
import { FinalErrorBoundary } from '@pp/core/src/components/errorBoundary/FinalErrorBoundary';
import { LayoutErrorBoundaryConnected } from '@pp/core/src/components/errorBoundary/LayoutErrorBoundaryConnected';
import { GameInitializer } from '@pp/core/src/components/gameRoot/GameInitialiser';
import { GameRootProps } from '@pp/core/src/components/gameRoot/GameRoot';
import { IconRoot } from '@pp/core/src/components/icons/iconRoot';
import { LobbyConnected } from '@pp/core/src/components/inGameLobby/LobbyConnected';
// import { LayoutRoot } from "@pp/core/src/components/layoutRoot/LayoutRoot";
import { LegacyContextProvider } from '@pp/core/src/components/legacyMode/LegacyContextProvider';
import { PopupConfigInterface, PopupConnected } from '@pp/core/src/components/popup/PopupConnected';
import { VolumeRestorer } from '@pp/core/src/components/settings/VolumeRestorer';
import { TooltipRoot } from '@pp/core/src/components/tooltip/TooltipRoot';
import { VideoRefContextProvider } from '@pp/core/src/components/video_v8/VideoRefContext';
import { GameConfigProvider } from '@pp/core/src/config/GameConfigProvider';
import { AudioControllerConnected } from '@pp/core/src/controllers/audioController/AudioControllerConnected';
import { BetOverriderConnector } from '@pp/core/src/controllers/betOverrider/BetOverriderConnector';
import { BrowserCacheControllerConnector } from '@pp/core/src/controllers/browserCacheController/BrowserCacheControllerConnector';
import { ChatCommunicationControllerConnector } from '@pp/core/src/controllers/chatCommunication/ChatCommunicationControllerConnector';
import { FreeChipConnector } from '@pp/core/src/controllers/freeChip/FreeChipConnector';
import { GameCommunicationControllerConnector } from '@pp/core/src/controllers/gameCommunication/GameCommunicationControllerConnector';
import { PerformanceControllerConnector } from '@pp/core/src/controllers/performanceController/PerformanceControllerConnector';
import { LobbyMessageReceiver } from '@pp/core/src/messages/LobbyMessageReceiver';
import { MessageConnector } from '@pp/core/src/messages/MessageConnector';
import { coreSoundResources } from '@pp/core/src/sounds';
import { CoreStoreProvider } from '@pp/core/src/store/CoreStore';
import { TelemetryConnector } from '@pp/core/src/telemetry/TelemetryConnector';

import { CrashGameBody } from '../../components/crashGameBody/CrashGameBody';
import {
  CrashAutoplayModifiedConnected,
  crashautoplayModifiedConnectedOnClose,
  CrashPopupType,
} from '../popup/CrashAutoplayModifiedConnected';

export const CrashGameRoot: FC<GameRootProps> = ({
  background,
  betLimitContent,
  children,
  config,
  gameSettingsSpecificTab,
  navControls,
  quickGuideContent,
  soundControls,
}): ReactElement => {
  const { gameCommunicationConfig, gameStore, resources, soundResources } = config;

  const popupsMap: Record<CrashPopupType, PopupConfigInterface> = {
    [CrashPopupType.CrashAutoplayModified_1]: {
      content: <CrashAutoplayModifiedConnected gameStore={gameStore} betBoardID={1} />,
      onClose: crashautoplayModifiedConnectedOnClose,
    },
    [CrashPopupType.CrashAutoplayModified_2]: {
      content: <CrashAutoplayModifiedConnected gameStore={gameStore} betBoardID={2} />,
      onClose: crashautoplayModifiedConnectedOnClose,
    },
  };
  return (
    <FinalErrorBoundary>
      <LegacyContextProvider>
        <CoreStoreProvider store={gameStore}>
          <VideoRefContextProvider>
            <LayoutErrorBoundaryConnected>
              <GameConfigProvider
                value={{
                  betLimitContent,
                  config,
                  gameSettingsSpecificTab,
                  navControls,
                  quickGuideContent,
                  soundControls,
                }}
              >
                {background}
                <GameInitializer />
                <JurisdictionNL />
                <SessionBalanceConnected />
                <VolumeRestorer />
                <IconRoot />
                <AudioControllerConnected audioFiles={[...coreSoundResources, ...soundResources]} />
                <GameCommunicationControllerConnector gameStore={gameStore} config={gameCommunicationConfig} />
                <ChatCommunicationControllerConnector gameStore={gameStore} />
                <BrowserCacheControllerConnector resources={resources} />
                <PerformanceControllerConnector />
                <BetOverriderConnector />
                <FreeChipConnector />
                <CrashGameBody>{children}</CrashGameBody>
                <TooltipRoot />
                <PopupConnected additionalPopups={popupsMap} />
                <MessageConnector />
                <LobbyMessageReceiver />
                <TelemetryConnector />
                <LobbyConnected />
                <BackToGameHolder>
                  <BackToGameConnected />
                </BackToGameHolder>
              </GameConfigProvider>
            </LayoutErrorBoundaryConnected>
          </VideoRefContextProvider>
        </CoreStoreProvider>
      </LegacyContextProvider>
    </FinalErrorBoundary>
  );
};
